import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Mail, RefreshCw, CheckCircle2, Clock, AlertCircle, 
  Search, Key, Calendar, User
} from 'lucide-react';
import api from '../services/api';

const CredentialsManager = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resending, setResending] = useState(null);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const res = await api.get('/admin/credentials-emails');
      setCredentials(res.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (userId, email) => {
    setResending(userId);
    try {
      await api.post(`/admin/credentials-emails/${userId}/resend`);
      toast.success(`Identifiants renvoyés à ${email}`);
      loadCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du renvoi');
    } finally {
      setResending(null);
    }
  };

  const filteredCredentials = credentials.filter(c => 
    c.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    c.user_nom?.toLowerCase().includes(search.toLowerCase()) ||
    c.user_prenom?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: credentials.length,
    envoyes: credentials.filter(c => c.envoye).length,
    mdp_changes: credentials.filter(c => c.mot_de_passe_change).length
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total envois</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Emails envoyés</p>
                <p className="text-3xl font-bold text-emerald-700">{stats.envoyes}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">MDP changés</p>
                <p className="text-3xl font-bold text-purple-700">{stats.mdp_changes}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Key className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Historique des Envois d'Identifiants
            </CardTitle>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10 rounded-xl w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredCredentials.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun envoi d'identifiants enregistré</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredCredentials.map((cred) => (
                <div key={cred.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                        {cred.user_prenom?.[0]}{cred.user_nom?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {cred.user_prenom} {cred.user_nom}
                        </p>
                        <p className="text-sm text-slate-500">{cred.user_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm">
                        <p className="text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {cred.date_envoi ? new Date(cred.date_envoi).toLocaleDateString('fr-FR') : 'Non envoyé'}
                        </p>
                        <Badge className={cred.user_role === 'bénéficiaire' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                          {cred.user_role}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        {cred.envoye ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Envoyé
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            En attente
                          </Badge>
                        )}
                        
                        {cred.mot_de_passe_change ? (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Key className="w-3 h-3 mr-1" />
                            MDP changé
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            MDP initial
                          </Badge>
                        )}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResend(cred.user_id, cred.user_email)}
                        disabled={resending === cred.user_id}
                        className="rounded-xl"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${resending === cred.user_id ? 'animate-spin' : ''}`} />
                        Renvoyer
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CredentialsManager;
